import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { environment } from './environments/environment';
import * as Sentry from '@sentry/angular';
import type { Breadcrumb, ErrorEvent } from '@sentry/angular';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import {
  ApiModule as AuthorityApiModule,
  Configuration as AuthorityConfiguration,
} from '@goatsports/authority/data-access';
import { TranslocoRootModule } from '@goatsports/shared/util';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorStateMatcher } from '@angular/material/core';
import { AppErrorStateMatcher } from './app/shared/app-matcher';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ServerHttpInterceptor } from './app/shared/interceptors/server-http.interceptor';
import { ErrorService } from './app/shared/services/error.service';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app/app.routes';

// Privacy scrubbers for the Sentry init below. Sentry is used strictly for
// error + performance diagnostics — we strip anything that could carry personal
// data before it leaves the browser: IP addresses, cookies, auth tokens,
// request/response bodies, and query strings.
function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (event.user) {
    // Keep only the pseudonymous id (§7.7g); strip direct identifiers.
    delete event.user.ip_address;
    delete event.user.email;
    delete event.user.username;
  }
  if (event.request) {
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.query_string;
    if (event.request.url) {
      event.request.url = event.request.url.split('?')[0];
    }
    if (event.request.headers) {
      delete event.request.headers['Authorization'];
      delete event.request.headers['Cookie'];
    }
  }
  return event;
}

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.data) {
    // Strip query strings from any URL-bearing breadcrumb — xhr/fetch carry
    // `url`, navigation carries `from`/`to` — since they can hold tokens or PII.
    for (const key of ['url', 'from', 'to']) {
      if (typeof breadcrumb.data[key] === 'string') {
        breadcrumb.data[key] = breadcrumb.data[key].split('?')[0];
      }
    }
    // Drop any captured request/response bodies from network breadcrumbs.
    delete breadcrumb.data['body'];
    delete breadcrumb.data['request_body'];
    delete breadcrumb.data['response_body'];
  }
  return breadcrumb;
}

if (environment.sentryDSN) {
  Sentry.init({
    dsn: environment.sentryDSN,
    release: 'goatsports@1.32.0',
    dist: '1',
    environment: environment.sentryEnvironment,
    // Session Replay is intentionally NOT enabled — it captures DOM content
    // (including credentials typed into the login form) and is disallowed by
    // our privacy policy. Keep Sentry to errors + performance tracing only.
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', environment.authorityApiUrl],
    sendDefaultPii: false,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    importProvidersFrom(
      AuthorityApiModule.forRoot(() => {
        return new AuthorityConfiguration({
          basePath: environment.authorityApiUrl,
          withCredentials: true,
        });
      }),
      TranslocoRootModule,
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(
      routes,
      // withPreloading(PreloadAllModules),
      withComponentInputBinding(),
    ),
    provideZoneChangeDetection({
      eventCoalescing: true,
    }),
    provideAnimationsAsync(),
    { provide: ErrorStateMatcher, useClass: AppErrorStateMatcher },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerHttpInterceptor,
      deps: [ErrorService],
      multi: true,
    },
  ],
});
